import type { ApiClient } from '../../core/api-client.js';
import { ApiError } from '../../core/api-client.js';
import type { Resultado } from '../../core/resultado.js';
import type { Ops, EtapaOps, PatchOps } from '@pgcustom/shared-types';

/** Filtros aceitos por `GET /ops` (`listarOpsQuerySchema`, `apps/api/src/modules/producao/ops.schema.ts`). */
export interface FiltroListaOps {
  etapa?: EtapaOps;
  tecnicoId?: string;
  pedidoId?: string;
  limit?: number;
  offset?: number;
}

/**
 * Adapter do módulo Produção no frontend — único ponto que conhece o
 * formato das rotas `/api/v1/ops/*`
 * (`apps/api/src/modules/producao/ops.routes.ts`). Mesmo padrão de
 * `PedidosApi`/`FinanceiroApi`: `listar` devolve `Resultado<T>`;
 * `obterPorId`/`atualizar`/`alterarEtapa` propagam `ApiError`. Sem
 * `criar` — OPS nasce só do listener de `pedido.pago` (nenhuma rota de
 * criação existe).
 */
export class OpsApi {
  constructor(private readonly api: ApiClient) {}

  async listar(filtro: FiltroListaOps): Promise<Resultado<Ops[]>> {
    try {
      const resposta = await this.api.get<{ ops: Ops[] }>('/ops', { ...filtro });
      if (resposta.ops.length === 0) return { status: 'vazio' };
      return { status: 'ok', dado: resposta.ops };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return {
        status: 'erro',
        mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar produção',
      };
    }
  }

  async obterPorId(id: string): Promise<Ops> {
    const resposta = await this.api.get<{ ops: Ops }>(`/ops/${id}`);
    return resposta.ops;
  }

  async atualizar(id: string, dado: PatchOps): Promise<Ops> {
    const resposta = await this.api.patch<{ ops: Ops }>(`/ops/${id}`, dado);
    return resposta.ops;
  }

  async alterarEtapa(id: string, etapa: EtapaOps): Promise<Ops> {
    const resposta = await this.api.post<{ ops: Ops }>(`/ops/${id}/etapa`, { etapa });
    return resposta.ops;
  }
}
